// Mide tiempo activo por pathname y encola page views (Fase 2).
// Se integra con React Router via useLocation en App.js (ver useTracking.js).
//
// Estrategia:
// - Cada vez que cambia el pathname, se cierra el page view anterior (con su active_seconds
//   acumulado) y se abre uno nuevo.
// - El tiempo se acumula solo mientras document.visibilityState === 'visible' (Visibility API),
//   para no contar tabs en background.
// - El último page view se flushea en pagehide/visibilitychange:hidden (delegado a trackingQueue).

import { enqueuePageview, flushPageviews } from "./trackingQueue";

let currentPathname = null;
let previousPathname = null;
let enteredAt = null;
let visibleSinceTs = null;
let accumulatedSeconds = 0;

function isVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

function nowTs() {
  return Date.now();
}

/**
 * Suma el tiempo transcurrido desde la última vez que se marcó "visible" (si corresponde).
 */
function accrueVisibleTime() {
  if (visibleSinceTs !== null) {
    accumulatedSeconds += Math.max(0, Math.round((nowTs() - visibleSinceTs) / 1000));
    visibleSinceTs = null;
  }
}

function handleVisibilityChange() {
  if (isVisible()) {
    visibleSinceTs = nowTs();
  } else {
    accrueVisibleTime();
  }
}

/**
 * Cierra el page view actual (si existe) y lo encola con el tiempo activo acumulado.
 */
function closeCurrentPageview({ deviceId, sessionUuid }) {
  if (currentPathname === null) return;

  accrueVisibleTime();

  enqueuePageview({
    session_uuid: sessionUuid,
    device_id: deviceId,
    pathname: currentPathname,
    referrer_path: previousPathname,
    entered_at: enteredAt,
    active_seconds: accumulatedSeconds,
  });
}

/**
 * Notifica un cambio de ruta. Cierra el page view anterior y abre uno nuevo para `pathname`.
 * Llamar desde un efecto atado a `location.pathname` (ver useTracking.js).
 */
export function trackPageview(pathname, { deviceId, sessionUuid }) {
  if (!deviceId || !sessionUuid || !pathname) return;
  if (pathname === currentPathname) return;

  closeCurrentPageview({ deviceId, sessionUuid });

  previousPathname = currentPathname;
  currentPathname = pathname;
  enteredAt = new Date().toISOString();
  accumulatedSeconds = 0;
  visibleSinceTs = isVisible() ? nowTs() : null;
}

/**
 * Arranca el listener de Visibility API. Idempotente.
 */
let listenerAttached = false;
export function startPageTracker() {
  if (listenerAttached || typeof document === "undefined") return;
  listenerAttached = true;
  visibleSinceTs = isVisible() ? nowTs() : null;
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

/**
 * Cierra el page view actual y flushea la cola — usar en pagehide / logout.
 */
export function finalizePageTracking({ deviceId, sessionUuid }) {
  closeCurrentPageview({ deviceId, sessionUuid });
  currentPathname = null;
  previousPathname = null;
  flushPageviews();
}
